import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2,
  Inbox,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export const LoadingRow = ({ colSpan = 1 }) => (
  <tr>
    <td colSpan={colSpan} className="p-8">
      <div className="flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    </td>
  </tr>
);

export const EmptyState = ({ message = "No records found.", icon: Icon = Inbox }) => (
  <div className="text-center py-12 text-slate-500" data-testid="empty-state">
    <Icon className="h-10 w-10 mx-auto text-slate-300" />
    <p className="mt-3 text-sm">{message}</p>
  </div>
);

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const readStoredNumber = (key, fallbackValue, options = {}) => {
  if (!key || typeof window === "undefined") {
    return fallbackValue;
  }

  const storedValue = window.sessionStorage.getItem(key);

  if (storedValue === null || storedValue === "") {
    return fallbackValue;
  }

  const numberValue = Number(storedValue);

  if (!Number.isFinite(numberValue)) {
    return fallbackValue;
  }

  if (options.min != null && numberValue < options.min) {
    return fallbackValue;
  }

  if (
    Array.isArray(options.allowedValues) &&
    !options.allowedValues.includes(numberValue)
  ) {
    return fallbackValue;
  }

  return numberValue;
};

/**
 * Generic data table with optional built-in client-side pagination and search.
 *
 * Backward compatibility:
 * - Existing tables work exactly the same.
 * - Page is reset when the table search or page size changes.
 *
 * Use `paginationStorageKey` only when a page should remember its table page
 * after navigating to a detail popup/page and returning.
 */
export const DataTable = ({
  columns,
  rows,
  loading,
  empty = "No records found.",
  testId,
  // Pagination (client-side)
  paginated = true,
  defaultPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  paginationStorageKey,
  paginationResetKey,
  // Built-in search (client-side). Pass `searchable` + `searchKeys` to enable.
  searchable = false,
  searchKeys = [],
  searchPlaceholder = "Search…",
}) => {
  const pageStorageKey = paginationStorageKey
    ? `bms:datatable:${paginationStorageKey}:page`
    : null;

  const pageSizeStorageKey = paginationStorageKey
    ? `bms:datatable:${paginationStorageKey}:pageSize`
    : null;

  const [page, setPageState] = useState(() =>
    readStoredNumber(pageStorageKey, 0, { min: 0 })
  );

  const [pageSize, setPageSizeState] = useState(() =>
    readStoredNumber(pageSizeStorageKey, defaultPageSize, {
      min: 1,
      allowedValues: pageSizeOptions,
    })
  );

  const [query, setQuery] = useState("");
  const previousPaginationResetKey = useRef(paginationResetKey);

  const setPage = useCallback((nextPage) => {
    setPageState((currentPage) => {
      const resolvedPage =
        typeof nextPage === "function" ? nextPage(currentPage) : nextPage;

      const safePage = Math.max(0, Number(resolvedPage) || 0);

      if (pageStorageKey) {
        window.sessionStorage.setItem(pageStorageKey, String(safePage));
      }

      return safePage;
    });
  }, [pageStorageKey]);

  const setPageSize = (nextPageSize) => {
    const numericPageSize = Number(nextPageSize);

    const safePageSize = pageSizeOptions.includes(numericPageSize)
      ? numericPageSize
      : defaultPageSize;

    setPageSizeState(safePageSize);

    if (pageSizeStorageKey) {
      window.sessionStorage.setItem(pageSizeStorageKey, String(safePageSize));
    }

    setPage(0);
  };

  const safeRows = Array.isArray(rows) ? rows : [];

  // Apply client-side search
  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return safeRows;

    const q = query.toLowerCase();

    return safeRows.filter((r) =>
      searchKeys.some((k) => {
        const v = r?.[k];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [safeRows, query, searchable, searchKeys]);

  const total = filtered.length;
  const totalPages = paginated ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  // If current page is outside the available range, move to the last valid page.
  useEffect(() => {
    if (loading) {
      return;
    }

    if (total === 0) {
      if (page > 0) {
        setPage(0);
      }
      return;
    }

    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [loading, total, page, totalPages, setPage]);

  // Refetches and parent rerenders can replace `rows` without changing the
  // user's table context (for example, opening or closing a dialog). Keep the
  // current page for those updates. Callers can change paginationResetKey for
  // an explicit external filter change that should return to page one.
  useEffect(() => {
    if (previousPaginationResetKey.current !== paginationResetKey) {
      previousPaginationResetKey.current = paginationResetKey;
      setPage(0);
    }
  }, [paginationResetKey, setPage]);

  const paginatedRows = paginated
    ? filtered.slice(page * pageSize, page * pageSize + pageSize)
    : filtered;

  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + pageSize);

  return (
    <div className="space-y-3" data-testid={testId ? `${testId}-wrap` : undefined}>
      {searchable && (
        <div className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              className="pl-9"
              data-testid={testId ? `${testId}-search` : "table-search"}
            />
          </div>
        </div>
      )}

      <div className="bms-card overflow-hidden" data-testid={testId}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-200 text-left">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading && <LoadingRow colSpan={columns.length} />}

              {!loading && paginatedRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>
                    <EmptyState message={empty} />
                  </td>
                </tr>
              )}

              {!loading &&
                paginatedRows.map((r, i) => (
                  <tr
                    key={r.id || r.key || i}
                    className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className="px-4 py-3 text-slate-700 align-middle"
                      >
                        {c.render ? c.render(r) : r[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {paginated && !loading && total > 0 && (
          <div
            className="no-print flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/40 px-3 py-2 text-xs text-slate-600"
            data-testid={testId ? `${testId}-pagination` : "table-pagination"}
          >
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline">Rows per page</span>

              <Select
                value={String(pageSize)}
                onValueChange={(v) => setPageSize(Number(v))}
              >
                <SelectTrigger
                  className="h-8 w-[78px]"
                  data-testid={testId ? `${testId}-pagesize` : "table-pagesize"}
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {pageSizeOptions.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <span className="text-slate-500">
                {from}–{to} of {total}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(0)}
                disabled={page === 0}
                className="h-8 w-8 rounded inline-flex items-center justify-center border border-slate-200 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-100"
                aria-label="First page"
                data-testid={testId ? `${testId}-first` : "table-first"}
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="h-8 w-8 rounded inline-flex items-center justify-center border border-slate-200 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-100"
                aria-label="Previous page"
                data-testid={testId ? `${testId}-prev` : "table-prev"}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <span className="px-2 font-medium text-slate-700">
                Page {page + 1} / {totalPages}
              </span>

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="h-8 w-8 rounded inline-flex items-center justify-center border border-slate-200 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-100"
                aria-label="Next page"
                data-testid={testId ? `${testId}-next` : "table-next"}
              >
                <ChevronRight className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => setPage(totalPages - 1)}
                disabled={page >= totalPages - 1}
                className="h-8 w-8 rounded inline-flex items-center justify-center border border-slate-200 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-100"
                aria-label="Last page"
                data-testid={testId ? `${testId}-last` : "table-last"}
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
